import { Inject, Injectable } from '@angular/core';
import { Branding, DefaultBranding } from '@app/portal/models/branding';
import { PreferenceStorageService } from '@app/shared/services/preference-storage.service';
import { Language } from '@app/translate/models/language';
import { resolveTranslation, Translatable } from '@app/translate/models/translatable';
import { Translation } from '@app/translate/models/translation';
import { EmptyTranslations, Translations } from '@app/translate/models/translations';
import { PortalTranslateHttpLoaderService } from '@app/translate/services/portal-translate-http-loader.service';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';
import JSZip from 'jszip';
import { BehaviorSubject, forkJoin, Observable, ReplaySubject, Subject } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';

interface TranslateMode {
  inTranslateMode: boolean;
  translations: Translations;
}

@Injectable({
  providedIn: 'root',
})
export class PortalTranslateService {
  // Default branding is neotel
  private branding: Branding = DefaultBranding;

  private translateModeSubject: Subject<TranslateMode> = new Subject();
  private translations: Translations = EmptyTranslations;
  private translations$: Subject<Translations> = new ReplaySubject(1);
  private _inTranslateMode: boolean = false;

  private _activeLanguage$ = new ReplaySubject<string>(1);

  get activeLanguage$(): Observable<string> {
    return this._activeLanguage$.asObservable();
  }

  get inTranslateMode() {
    return this._inTranslateMode;
  }

  get languageKeys() {
    return this.httpLoaderService.languageKeys;
  }

  private _languageKeys$ = new BehaviorSubject<string[]>([]);

  get languageKeys$() {
    return this._languageKeys$.asObservable();
  }

  get translateModeChanged() {
    return this.translateModeSubject;
  }

  constructor(
    @Inject(TranslateLoader) private httpLoaderService: PortalTranslateHttpLoaderService,
    private preferenceStorageService: PreferenceStorageService,
    private translateService: TranslateService
  ) {
    this.httpLoaderService.fetchLanguageKeys().subscribe((languageKeys) => {
      this._languageKeys$.next(languageKeys);
    });

    this.translateService.onLangChange.subscribe((event) => {
      this._activeLanguage$.next(event.lang);
    });

    this._activeLanguage$.next(this.translateService.currentLang);
  }

  /**
   * Create a zip containing all translations
   */
  createTranslationZip(): Observable<Blob> {
    return this.fetchTranslations().pipe(
      mergeMap((translations) => {
        const zip = new JSZip();

        const general = zip.folder('general');
        const specific = zip.folder(this.branding.themeName);

        translations.languageKeys.forEach((languageKey) => {
          general.file(`${languageKey}.json`, JSON.stringify(translations.getLanguage(languageKey)));

          specific.file(`${languageKey}.json`, JSON.stringify(translations.getOverrides(languageKey)));
        });

        return zip.generateAsync({ type: 'blob' }) as Promise<Blob>;
      })
    );
  }

  enrich(key: string | string[], translation: string) {
    let returnValue = translation;

    if (Array.isArray(key)) {
      key = key.join('<!-- test -->;;;');
    }

    if (this._inTranslateMode) {
      returnValue = key;
    }

    return returnValue;
  }

  /**
   * Fetch translations. This will:
   *
   *  1. Fetch a list of languages for the current branding
   *  2. Download generic translations from the portal and branding specific translations from the branding
   *  3. Merge the two translations into a Translations object
   *  4. Refresh the translate$ observable
   */
  fetchTranslations(forceReload: boolean = false): Observable<Translations> {
    if (forceReload || this.translations === EmptyTranslations) {
      const translationKeys$: Observable<string[]> = this.httpLoaderService.fetchLanguageKeys();

      translationKeys$
        .pipe(
          mergeMap((languageKeys) => {
            this._languageKeys$.next(languageKeys);

            const languages$: Observable<Language[]> = this.fetchLanguages(languageKeys);
            const overrides$: Observable<Language[]> = this.fetchOverrides(languageKeys);

            return forkJoin([languages$, overrides$]).pipe(
              map(([languages, overrides]) => {
                // Use the first language as reference (i.e. only the keys from the first language will be handled)
                const referenceLang = languages[0];
                const translationMap: { [key: string]: Translation } = {};

                Object.keys(referenceLang)
                  .map((entry: string) => {
                    const translation: Translation = new Translation(entry);

                    languageKeys.map((key, index) => {
                      translation.overrides[key] = overrides[index][entry];
                      translation.translations[key] = languages[index][entry];
                    });

                    return translation;
                  })
                  .forEach((translation) => {
                    translationMap[translation.key] = translation;
                  });

                languageKeys.forEach((key, index) => {
                  this.translateService.setTranslation(key, languages[index], true);
                });

                return new Translations(translationMap, languageKeys, languages);
              })
            );
          })
        )
        .subscribe((translations) => {
          this.translations = translations;

          this.translations$.next(translations);
        });
    }

    return this.translations$;
  }

  getTranslation(key: string): Translation {
    return this.translations.item(key);
  }

  runTranslator() {
    if (!this.inTranslateMode) {
      this.fetchTranslations();

      this.translations$.pipe(first()).subscribe((translations: Translations) => {
        this._inTranslateMode = true;
        this.translations = translations;

        this.translateModeSubject.next({
          inTranslateMode: true,
          translations: this.translations,
        });
      });
    } else {
      this._inTranslateMode = false;

      this.translateModeSubject.next({
        inTranslateMode: false,
        translations: EmptyTranslations,
      });
    }
  }

  setBranding(branding: Branding): void {
    this.branding = branding;
    this.httpLoaderService.setBranding(branding);
  }

  /**
   * Translate the given translatable using the current language of the user.
   */
  translate(translations: Translatable): string;

  /**
   * Translate the given translation and pass the additional params.
   * @deprecated You should use translate(translatable) without passing any additional params. This function exists solely for backwards-compatibility.
   * @param translation
   * @param params
   */
  translate(translation: Translatable, params?: Record<string, any>): string;

  translate(translation: Translatable, params?: Record<string, any>): string {
    const { key, parameters, parametersToTranslate } = resolveTranslation(translation, params);
    const allParameters = {
      ...parameters,
    };

    if (parametersToTranslate != null) {
      Object.entries(parametersToTranslate).forEach(([key, value]) => {
        allParameters[key] = this.translate(value);
      });
    }

    return this.translateService.instant(key, allParameters);
  }

  updateTranslation(translation: Translation) {
    this.translations.updateTranslation(translation);

    translation.entries
      .map((entry) => {
        // Convert empty values to undefined
        if (entry[1] === '') {
          entry[1] = undefined;
        }

        return entry;
      })
      .forEach(([language, value]) => {
        this.translations.getLanguage(language)[translation.key] = value;

        this.translateService.setTranslation(
          language,
          {
            [translation.key]: value,
          },
          true
        );
      });

    this.translations$.next(this.translations);
  }

  use(language: string): void {
    this.preferenceStorageService.updatePreference('preferredLanguage', language);
    this.translateService.use(language);
  }

  /**
   * Fetch specified languages.
   */
  private fetchLanguages(languageKeys: string[]): Observable<Language[]> {
    return forkJoin(this.httpLoaderService.fetchAll(languageKeys));
  }

  /**
   * Fetch specified languages from the current branding.
   */
  private fetchOverrides(languageKeys: string[]): Observable<Language[]> {
    return forkJoin(this.httpLoaderService.fetchBrandingTranslations(languageKeys));
  }
}
